import React from 'react';
import { Mail, MessageSquare } from 'lucide-react';

/**
 * Segmented control for choosing how the 2FA login code is delivered.
 *
 * Shared by DeveloperLogin and SuperAdminLogin. The server validates the
 * choice after the password check -- picking "Text message" without a phone
 * number on the account comes back as a 400 with a message pointing at Profile.
 *
 * `accent` matches the host page's theme: 'primary' (developer) or 'purple'
 * (super admin).
 */
const TwoFactorMethodSelector = ({
  value,
  onChange,
  disabled = false,
  accent = 'primary',
  smsEnabled = true,
}) => {
  // Nothing to choose from when the server cannot send SMS -- email is the
  // only channel, so the control would just be a dead button.
  if (!smsEnabled) return null;

  const options = [
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'sms', label: 'Text message', icon: MessageSquare },
  ];

  const activeClasses =
    accent === 'purple'
      ? 'bg-purple-500/15 border-purple-500/40 text-purple-500'
      : 'bg-primary/15 border-primary/40 text-primary';

  return (
    <div className="space-y-2">
      <span className="text-sm font-medium text-foreground">
        Send my verification code by
      </span>
      <div className="grid grid-cols-2 gap-2">
        {options.map((option) => {
          const Icon = option.icon;
          const isActive = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange(option.id)}
              disabled={disabled}
              aria-pressed={isActive}
              className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isActive
                  ? activeClasses
                  : 'bg-background border-border text-muted-foreground hover:text-foreground hover:bg-muted/40'
              }`}
            >
              <Icon className="w-4 h-4" />
              {option.label}
            </button>
          );
        })}
      </div>
      {value === 'sms' && (
        <p className="text-xs text-muted-foreground">
          Sent to the phone number saved on your profile. Standard message rates may apply.
        </p>
      )}
    </div>
  );
};

export default TwoFactorMethodSelector;
